import { useTranslation } from "react-i18next";
import produce from "immer";
import {
  Box,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import SelectField from "components/Forms/SelectField";
import { useRuleContext } from "pages/Rules/Form/context/ruleContext";

import { RequestApproval } from "@dashboard-v3/api";
import { State } from "pages/Rules/Form/context/types";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";
import { TFunction } from "i18next";

type ExpirationType = RequestApproval["expiration"]["type"];

export default function RequestExpiration() {
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { expiration } = getApprovalSteps(state.rule);
  const { isValid, error } = getValidationErrors(state, t);

  const onChange = e => {
    const { name, value } = e.target;
    const update = produce(expiration, draft => {
      const update = name === "value" ? Number(value) : value;
      draft[name] = update;
    });

    dispatch({
      type: "updateStep",
      payload: {
        step: "approvalSteps",
        changes: { expiration: update },
      },
    });
  };

  const getMinMax = () => {
    const { type } = expiration;
    if (type === "DAYS") return { max: "30", min: "1" };
    if (type === "HOURS") return { max: "24", min: "1" };
  };

  return (
    <Box mb={2} style={{ maxWidth: "80%" }}>
      <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
        {t("approvalSteps.expiration.title")}
      </Typography>
      <Typography display="block" variant="caption" color="textSecondary">
        {t("approvalSteps.expiration.desc")}
      </Typography>
      <Grid container spacing={2} style={{ marginTop: 10 }}>
        <Grid item lg={6}>
          <TextField
            fullWidth
            name="value"
            type="number"
            variant="outlined"
            label={t("approvalSteps.expiration.durationLabel")}
            value={expiration.value}
            onChange={onChange}
            inputProps={{ ...getMinMax() }}
            error={!isValid}
          />
        </Grid>
        <Grid item lg={6}>
          <SelectField<ExpirationType>
            name="type"
            label={t("approvalSteps.expiration.unitLabel")}
            options={["DAYS", "HOURS"]}
            value={expiration.type}
            onChange={onChange}
            renderOption={opt => (
              <MenuItem key={opt} value={opt}>
                {t(`approvalSteps.expiration.unit.${opt}`)}
              </MenuItem>
            )}
          />
        </Grid>
      </Grid>
      <FormHelperText margin="dense" error>
        {error}
      </FormHelperText>
    </Box>
  );
}

function getValidationErrors(state: State, t: TFunction) {
  const { steps } = state;
  const { expiration } = getApprovalSteps(state.rule);
  const errors = steps.approvalSteps.error as string[];

  if (!hasError(errors)) return { isValid: true, error: "" };

  if (errors.includes("EXCEEDS_VALUE_MAX")) {
    const error = t("approvalSteps.expiration.maxValueExceeded", {
      limitValue: expiration.type === "HOURS" ? 24 : 30,
      limitType: t(`sendDelay.${expiration.type}`),
    });

    return { isValid: false, error };
  }

  if (errors.includes("EXCEEDS_VALUE_MIN")) {
    const error = t("approvalSteps.expiration.minValueRequired", {
      limitValue: 1,
      limitType: t(`sendDelay.${expiration.type}`),
    });

    return { isValid: false, error };
  }

  if (errors.includes("EXCEEDS_UNIT_MAX")) {
    const error = t("approvalSteps.expiration.exceedsUnitMaxError", {
      limitValue: 24,
      limitType: t("sendDelay.HOURS"),
      nextType: t("sendDelay.DAYS"),
    });

    return { isValid: false, error };
  }

  if (errors.includes("EXCEEDS_UNIT_MIN")) {
    const error = t("approvalSteps.expiration.belowUnitMinError", {
      limitValue: 1,
      limitType: t("sendDelay.DAYS"),
      nextType: t("sendDelay.HOURS"),
    });

    return { isValid: false, error };
  }

  return { isValid: true, error: "" };
}

function hasError(error: unknown) {
  const expirationErrors = [
    "EXCEEDS_VALUE_MIN",
    "EXCEEDS_VALUE_MAX",
    "EXCEEDS_UNIT_MAX",
    "EXCEEDS_UNIT_MIN",
  ];

  return Array.isArray(error) && error.some(e => expirationErrors.includes(e));
}
